import { getServices } from '../../../infra/startup';
import bindAllMethods from '../../../utils/bindAllMethods';
import { internalLogger } from '../clientOsLogger';
import { EnvironmentVariableContextNotFoundError } from './types';
import { IEnvironmentVariablesService } from '../../../services/environmentVariablesService/IEnvironmentVariablesService';
import {
  EnvironmentVariablesInterfaceV1Type,
  SerializableObject
} from '@clientos/types';

/**
 * Environment Variables Interface V1 Implementation
 *
 * Provides public API access to environment variables from the manifest.
 * This interface acts as a facade over the EnvironmentVariablesService.
 */
export default class EnvironmentVariablesInterfaceV1 {
  private _environmentVariablesService: IEnvironmentVariablesService;

  constructor() {
    const services = getServices();
    this._environmentVariablesService = services.environmentVariablesService;
    bindAllMethods(this);
  }

  /**
   * Returns the public interface
   * @returns The environment variables interface
   */
  public getInterface(): EnvironmentVariablesInterfaceV1Type {
    return {
      get: this.get,
      listContexts: this.listContexts
    };
  }

  /**
   * Retrieves environment variables for a specific context
   * @param context The context name to retrieve variables for
   * @returns Promise resolving to immutable environment variables object
   * @throws EnvironmentVariableContextNotFoundError when context doesn't exist
   */
  public async get(context: string): Promise<SerializableObject> {
    try {
      internalLogger?.debug('Environment variables get requested', { context });

      const result = await this._environmentVariablesService.get(context);

      internalLogger?.debug('Environment variables retrieved successfully', {
        context,
        variablesCount:
          result && typeof result === 'object' ? Object.keys(result).length : 0
      });

      return result;
    } catch (error) {
      if (error instanceof EnvironmentVariableContextNotFoundError) {
        internalLogger?.warn('Environment variable context not found', {
          context,
          error: error.message
        });
      } else {
        internalLogger?.error('Failed to get environment variables', {
          context,
          error
        });
      }
      throw error;
    }
  }

  /**
   * Lists all available environment variable contexts
   * @returns Promise resolving to array of available context names
   */
  public async listContexts(): Promise<string[]> {
    try {
      internalLogger?.debug('Environment variable contexts list requested');

      const result = await this._environmentVariablesService.listContexts();

      internalLogger?.debug(
        'Environment variable contexts listed successfully',
        {
          contextsCount: result.length
        }
      );

      return result;
    } catch (error) {
      internalLogger?.error(
        'Failed to list environment variable contexts',
        error
      );
      throw error;
    }
  }
}
